import React from "react";
import { Shield, Lock, FileText, Mail, MapPin, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PrivacyPolicy() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="7399" id="compliance-privacy" ref={ref} className={`relative w-full bg-background py-24 transition-opacity duration-700 dark ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-[800px] mx-auto">
          {}
          <div className="mb-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-sm bg-primary/10 text-primary mb-6 backdrop-blur-sm border border-primary/20">
              <Shield className="w-8 h-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
              Privacy <span className="text-primary">& Compliance</span>
            </h1>
            <p className="text-lg text-muted-foreground font-default">
              Last updated: May 15, 2024
            </p>
          </div>

          {}
          <div className="bg-white/5 backdrop-blur-md border border-border/50 rounded-sm p-8 md:p-12 shadow-md">
            <div className="space-y-10 text-muted-foreground leading-relaxed font-default">
              
              {}
              <div>
                <h2 className="text-2xl font-default font-bold text-foreground mb-4 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  1. Overview
                </h2>
                <p className="mb-4">
                  At <strong>WorldAccess</strong>, we prioritize the protection of your digital footprint. This policy details how we manage, process, and protect your data when you use our crypto-enabled digital services, connectivity tools, and privacy infrastructure.
                </p>
                <p>
                  We operate with a privacy-first mindset, adhering to the General Data Protection Regulation (GDPR) and global encryption standards to ensure your anonymity and security across our platform.
                </p>
              </div>

              {}
              <div>
                <h2 className="text-2xl font-default font-bold text-foreground mb-4 flex items-center gap-3">
                  <Lock className="w-5 h-5 text-primary" />
                  2. Data Controller
                </h2>
                <p className="mb-4">
                  The entity responsible for processing data within the WorldAccess ecosystem is:
                </p>
                <div className="bg-background/50 p-6 rounded-sm border border-border/50 space-y-3">
                  <p className="font-default font-semibold text-foreground text-lg">WorldAccess Digital Ltd.</p>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>Global Operations / Digital-First Entity</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-primary shrink-0" />
                    <span className="font-mono">Secure Node: +44 20 7946 0000</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-primary shrink-0" />
                    <Link to="mailto:compliance@worldaccess.io" className="hover:text-primary transition-colors font-mono">
                      compliance@worldaccess.io
                    </Link>
                  </div>
                </div>
              </div>

              {}
              <div>
                <h2 className="text-2xl font-default font-bold text-foreground mb-4">
                  3. Information We Process
                </h2>
                <p className="mb-4">
                  We collect minimal data necessary to provide high-performance digital services. This information is provided during service acquisition or technical configuration:
                </p>
                <ul className="list-disc pl-6 space-y-2 marker:text-primary">
                  <li><strong>Account Metadata:</strong> Email address (if provided) and communication preferences.</li>
                  <li><strong>Technical Specs:</strong> VPS configurations, eSIM activation codes, and encrypted session logs.</li>
                  <li><strong>Financial Data:</strong> Wallet addresses and transaction hashes (no private keys are ever stored).</li>
                  <li><strong>Support Interactions:</strong> Encrypted records of technical support via secure channels.</li>
                </ul>
              </div>

              {}
              <div>
                <h2 className="text-2xl font-default font-bold text-foreground mb-4">
                  4. Service Utilization
                </h2>
                <p className="mb-4">
                  Data utilization is restricted to the functional requirements of our digital store:
                </p>
                <ul className="list-disc pl-6 space-y-2 marker:text-primary">
                  <li>To provision and maintain SaaS tools, VPS instances, and connectivity services.</li>
                  <li>To verify blockchain transactions and automate digital asset delivery.</li>
                  <li>To notify users of critical security updates or service expiration.</li>
                  <li>To optimize our infrastructure for privacy-oriented users and digital nomads.</li>
                  <li>To maintain compliance with international AML/KYC regulations where applicable.</li>
                </ul>
              </div>

              {}
              <div>
                <h2 className="text-2xl font-default font-bold text-foreground mb-4">
                  5. Infrastructure Partners
                </h2>
                <p>
                  We do not monetize user data. We only share essential information with verified infrastructure partners to facilitate service delivery:
                </p>
                <ul className="list-disc pl-6 mt-4 space-y-2 marker:text-primary">
                  <li><strong>Node Providers:</strong> To host your VPS and technical infrastructure.</li>
                  <li><strong>Carriers:</strong> To facilitate global roaming and eSIM connectivity.</li>
                  <li><strong>Legal Compliance:</strong> Only when presented with a valid international warrant or legal mandate.</li>
                </ul>
              </div>

              {}
              <div>
                <h2 className="text-2xl font-default font-bold text-foreground mb-4">
                  6. Your Digital Rights
                </h2>
                <p className="mb-4">
                  Under global privacy frameworks, you retain full control over your digital identity on our platform:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {["Right to Anonymity", "Right to Data Erasure", "Right to Portability", "Right to Access Logs", "Right to Object", "Right to Encryption"].map((right, i) => <div key={i} data-index={i} className="flex items-center gap-2 p-3 rounded-sm bg-background/40 border border-border/30">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      <span className="font-default font-medium text-foreground">{right}</span>
                    </div>)}
                </div>
              </div>

              {}
              <div className="pt-8 border-t border-border/50">
                <h2 className="text-2xl font-default font-bold text-foreground mb-4">
                  7. Contact Compliance
                </h2>
                <p className="mb-6">
                  If you have concerns regarding your privacy or wish to request a permanent data purge, please reach out to our team.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                    <Link to="#">
                      Open Secure Ticket
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="border-primary/20 hover:bg-primary/5 rounded-sm">
                    <Link to="mailto:compliance@worldaccess.io">
                      Email Data Officer
                    </Link>
                  </Button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>;
}